import { Head, Link } from "@inertiajs/react";
import { Box, Button, Container, Flex, Grid, Heading, Section, Text } from "@radix-ui/themes";
import { Instagram, Link as LinkIcon, MapPin } from "lucide-react";
import { ClubsIndexClub } from "@/types/shared";

interface Props {
  clubs: ClubsIndexClub[];
}

export default function ClubsIndex({ clubs }: Props) {
  return (
    <>
      <Head title="Clubs" />
      <Section size="1" px="4">
        <Container size="4">
          <Heading
            size="8"
            className="mb-6 text-xl md:text-2xl lg:text-4xl"
          >
            Clubs
          </Heading>

          {clubs.length === 0 ? (
            <Text color="gray">No clubs yet.</Text>
          ) : (
            <Flex direction="column" gap="6">
              {clubs.map((club) => (
                <ClubCard key={club.id} club={club} />
              ))}
            </Flex>
          )}
        </Container>
      </Section>
    </>
  );
}

function ClubCard({ club }: { club: ClubsIndexClub }) {
  return (
    <Box className="rounded-lg border-t-2 border-t-accent9 p-4 shadow-xl">
      <Grid columns={{ initial: "1", md: "12" }} gap="4">
        <Box gridColumn={{ md: "span 4" }}>
          {club.image_url ? (
            <img
              src={club.image_url}
              alt={`${club.name} logo`}
              className="rounded-lg shadow-2xl w-full object-cover"
            />
          ) : (
            <div className="w-full aspect-[3/2] rounded-lg bg-[image:var(--placeholder-gradient)]" />
          )}
        </Box>

        <Box gridColumn={{ md: "span 8" }}>
          <Flex direction="column" gap="3" height="100%">
            <Heading size="7">{club.name}</Heading>

            <Flex direction="column" gap="2">
              {club.locale && (
                <Flex align="center" gap="1">
                  <MapPin size={14} className="text-gray9" />
                  <Text size="2" color="gray">
                    {club.locale}
                  </Text>
                </Flex>
              )}

              {club.description && (
                <Text size="2" color="gray" className="line-clamp-3" dangerouslySetInnerHTML={{ __html: club.description }} />
              )}

              {(club.instagram || club.website) && (
                <Flex direction="column" gap="1" mt="2">
                  {club.instagram && (
                    <a
                      href={`https://instagram.com/${club.instagram.replace(/^@+/, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent9 hover:underline inline-flex items-center gap-1 text-sm"
                    >
                      <Instagram size={14} />
                      {club.instagram}
                    </a>
                  )}
                  {club.website && (
                    <a
                      href={club.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent9 hover:underline inline-flex items-center gap-1 text-sm"
                    >
                      <LinkIcon size={14} />
                      {club.website}
                    </a>
                  )}
                </Flex>
              )}
            </Flex>

            <Box mt="auto" pt="4">
              <Button asChild variant="outline" size="3" className="w-full">
                <Link href={`/clubs/${club.slug}`}>Learn More</Link>
              </Button>
            </Box>
          </Flex>
        </Box>
      </Grid>
    </Box>
  );
}
